import { AlertTriangle, CheckCircle2, CircleAlert, Loader2, XCircle } from 'lucide-react';
import { stepName, type CampaignSpec } from '@vagusx/shared';
import type { LaunchCheck, LaunchReadiness } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { useDebounce } from '@/hooks/use-debounce';
import { useLaunchCheck } from '@/hooks/use-outreach';
import { cn } from '@/lib/utils';
import { stepIndexOf, type Refusal } from './refusal';

/**
 * Launch (ADR-318, P11): every check the server runs before a campaign may
 * call, on the spec as it stands in the editor. Activate runs the same check
 * on the server and refuses with every failing one, so this is the author's
 * view of the gate, not the gate.
 *
 * `refusal` is that server answer when activate was refused: it is shown
 * instead of the editor's own check, because it is the one that decided.
 */
export function LaunchStep({
  spec,
  refusal,
  active,
}: {
  spec: CampaignSpec;
  refusal: Refusal | null;
  /** The Launch tab is showing; the check runs only then. */
  active: boolean;
}): React.ReactElement {
  const debounced = useDebounce(spec, 400);
  const check = useLaunchCheck(debounced, { enabled: active && !refusal });
  const readiness = refusal ? refusal.readiness : (check.data ?? null);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <section className="space-y-3 rounded-lg border p-4 lg:col-span-2">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-sm font-medium">Ready to launch?</h3>
          {check.isFetching && !refusal && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
          {readiness && <ReadinessBadge readiness={readiness} />}
        </div>

        {refusal && (
          <p className="flex items-start gap-1.5 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            The server refused this. Fix what is marked below, then try again.
          </p>
        )}
        {refusal && refusal.fieldErrors.length > 0 && (
          <FieldErrorList errors={refusal.fieldErrors} spec={spec} />
        )}
        {check.isError && !refusal && (
          <p className="text-sm text-destructive">Could not check this campaign.</p>
        )}
        {readiness && <LaunchChecks readiness={readiness} spec={spec} />}
      </section>

      <section className="h-fit space-y-2 rounded-lg border p-4">
        <h3 className="text-sm font-medium">Launching means</h3>
        <p className="text-sm text-muted-foreground">
          The campaign is published and started. First calls go out at the next open calling window,
          and anyone who qualifies later joins on their own. Pause at any time: everyone is held
          where they are.
        </p>
        <p className="text-xs text-muted-foreground">
          A patient already in the campaign stays on the version they started on.
        </p>
      </section>
    </div>
  );
}

/** Publish's field errors, each named by the step it is about. */
export function FieldErrorList({
  errors,
  spec,
}: {
  errors: Refusal['fieldErrors'];
  spec: CampaignSpec;
}): React.ReactElement {
  return (
    <ul className="divide-y">
      {errors.map((e) => {
        const index = stepIndexOf(e.field);
        const step = index !== null ? spec.sequence[index] : undefined;
        return (
          <li key={e.field} className="flex items-start gap-2.5 py-2" data-level="fail">
            <LevelIcon level="fail" />
            <span className="text-sm">
              {step ? `${stepName(step, index!)}: ` : ''}
              {e.message}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ReadinessBadge({ readiness }: { readiness: LaunchReadiness }): React.ReactElement {
  const failing = readiness.checks.filter((c) => c.level === 'fail').length;
  return readiness.ok ? (
    <Badge variant="secondary">Ready</Badge>
  ) : (
    <Badge variant="destructive">{failing} to fix</Badge>
  );
}

const ORDER: Record<LaunchCheck['level'], number> = { fail: 0, warn: 1, pass: 2 };

/** Failures first, then warnings, then what passed; each names its step. */
export function LaunchChecks({
  readiness,
  spec,
}: {
  readiness: LaunchReadiness;
  spec: CampaignSpec;
}): React.ReactElement {
  const checks = [...readiness.checks].sort((a, b) => ORDER[a.level] - ORDER[b.level]);
  const stepLabel = (stepId: string | undefined): string | null => {
    if (!stepId) return null;
    const index = spec.sequence.findIndex((s) => s.id === stepId);
    return index === -1 ? null : stepName(spec.sequence[index]!, index);
  };

  return (
    <ul className="divide-y">
      {checks.map((c, i) => {
        const step = stepLabel(c.stepId);
        return (
          <li
            key={`${c.code}-${c.stepId ?? ''}-${i}`}
            className="flex items-start gap-2.5 py-2"
            data-level={c.level}
          >
            <LevelIcon level={c.level} />
            <div className="min-w-0 text-sm">
              <span className={cn(c.level === 'pass' && 'text-muted-foreground')}>
                {step ? `${step}: ` : ''}
                {c.message}
              </span>
              {c.detail && <p className="text-xs text-muted-foreground">{c.detail}</p>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function LevelIcon({ level }: { level: LaunchCheck['level'] }): React.ReactElement {
  if (level === 'fail') return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />;
  if (level === 'warn') return <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />;
  return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />;
}
